import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode } from 'react';

import type { MessageProps } from './Messages';

const PILL_LAYOUT_CLASSNAME = 'inline-block rounded-full mx-2xs px-xs py-2xs mdMin:mx-xs mdMin:px-sm mdMin:py-xs';
const OVERLAY_PILL_LAYOUT_CLASSNAME = 'inline-block max-w-full rounded-full px-xs py-2xs backdrop-blur-[2px]';

export type PillColor = 'blue' | 'green' | 'grey' | 'red';

// Color semantics used across message types: blue = default/informational, gray = neutral
// or competing-bidder info, green = the current user's own bid/win, red = critical/urgent (missives).
const PILL_COLOR_CLASSNAMES: Record<PillColor, string> = {
    blue: 'border border-info-500 bg-background-info-light',
    green: 'border border-success-700 bg-background-success-light',
    grey: 'border border-border-secondary bg-background-subdued',
    red: 'border border-critical-500 bg-background-primary',
};

const getPillClassName = (color: PillColor) => cn(PILL_LAYOUT_CLASSNAME, PILL_COLOR_CLASSNAMES[color]);

const getPillColorClassName = (color: PillColor) => PILL_COLOR_CLASSNAMES[color];

export type PillAlign = 'left' | 'right';

type PillMessageOptions = {
    align?: PillAlign;
    color: PillColor;
    textColor?: 'critical' | 'primary' | 'secondary';
};

/** Shared single-message slot: every message type renders one of these, left- or right-aligned, keyed by `created`. */
export const renderMessageContainer = (created: number, align: PillAlign, content: ReactNode) => (
    <div
        className={align === 'right' ? 'text-right' : 'text-left'}
        key={created}
    >
        {content}
    </div>
);

export const renderPillMessage = (
    { created, overlay }: MessageProps,
    { align = 'left', color, textColor = 'primary' }: PillMessageOptions,
    content: ReactNode
) =>
    renderMessageContainer(
        created,
        align,
        <BodySecondary
            className={
                overlay ? cn(OVERLAY_PILL_LAYOUT_CLASSNAME, getPillColorClassName(color)) : getPillClassName(color)
            }
            color={textColor}
        >
            {content}
        </BodySecondary>
    );
